import { Component, EventEmitter, Input, OnInit, Optional, Output } from "@angular/core";
import { InputType } from "@gloria-public/questionnaire/domain/interfaces/questionnaire-common.interface";
import { IAnswer, IAnswersQuestion } from "@gloria-public/questionnaire/domain/interfaces/questionnaire-response.interface";
import { IQuestion } from "@gloria-public/questionnaire/domain/interfaces/questionnaire.interface";
import { ICoding } from "@shared/interfaces/shared.interface";
import { format, parse } from "date-fns";

@Component({
  selector: "app-encode-question",
  templateUrl: "./encode-question.component.html",
  styleUrls: ["./encode-question.component.scss"],
})
export class EncodeQuestionComponent implements OnInit {
  @Input() question!: IQuestion;
  @Input() response!: IAnswersQuestion;
  @Input() choices: ICoding[] = [];
  @Optional() @Input() viewAnswerOnly = false;

  @Output() answerChange = new EventEmitter<void>();

  InputType = InputType;

  ratingStars: number[] = [];
  ratingValue = 0;

  ngOnInit(): void {
    // Initialize empty answer array if needed
    if (!this.response.answer) {
      this.response.answer = [];
    }

    if ((this.question.answerDisplay?.type || this.question.type) === InputType.RATING) {
      this.ratingStars = this.getRatingStars();
      this.ratingValue = this.getRatingValue();
    }
  }

  // Choice inputs (radio, checkboxes, dropdown)
  selectSingleChoice(value: string): void {
    this.response.answer = [];
    const choice = this.choices.find((c) => c.code === value);

    const answer: IAnswer = {
      valueCoding: {
        code: value,
        display: choice?.display || value,
        system: choice?.system,
      },
    };

    this.response.answer.push(answer);
    this.answerChange.emit();
  }

  toggleMultipleChoice(value: string): void {
    if (!this.response.answer) {
      this.response.answer = [];
    }

    const existingIndex = this.response.answer.findIndex((a) => a.valueCoding.code === value);

    if (existingIndex > -1) {
      // Remove if already selected
      this.response.answer.splice(existingIndex, 1);
    } else {
      // Add if not selected
      const choice = this.choices.find((c) => c.code === value);
      const answer: IAnswer = {
        valueCoding: {
          code: value,
          display: choice?.display || value,
          system: choice?.system,
        },
      };
      this.response.answer.push(answer);
    }

    this.answerChange.emit();
  }

  isChoiceSelected(value: string): boolean {
    return this.response.answer?.some((a) => a.valueCoding.code === value) || false;
  }

  // Range/Scale input
  onRangeChange(value: number): void {
    this.response.answer = [];

    // Find the corresponding choice if it exists
    const choice = this.choices.find((c) => c.code === value.toString());

    const answer: IAnswer = {
      valueCoding: {
        code: value.toString(),
        display: choice?.display || value.toString(),
        system: choice?.system,
      },
    };

    this.response.answer.push(answer);
    this.answerChange.emit();
  }

  getRangeValue(): number {
    const value = this.response.answer?.[0]?.valueCoding.code;
    if (value) {
      return Number(value);
    }
    return Number(this.question.answerDisplay?.default || this.question.answerDisplay?.min || 0);
  }

  // Text inputs
  onTextChange(value: string): void {
    this.response.answer = [];

    const answer: IAnswer = {
      valueCoding: {
        code: value,
        display: value,
        system: "http://example.com",
      },
    };

    this.response.answer.push(answer);
    this.answerChange.emit();
  }

  // Number inputs
  onNumberChange(value: string, isUnit = false, index = 0): void {
    if (!this.response.answer) {
      this.response.answer = [];
    }

    // If this is a unit selection for number+unit input
    if (isUnit && index === 1) {
      if (this.response.answer.length === 0) {
        // Set default number value first
        const defaultValue = this.question.answerDisplay?.default || "0";
        this.response.answer.push({
          valueCoding: {
            code: defaultValue,
            display: defaultValue,
            system: "http://example.com",
          },
        });
      }

      const choice = this.choices.find((c) => c.code === value);
      if (this.response.answer.length === 1) {
        this.response.answer.push({
          valueCoding: {
            code: value,
            display: choice?.display || value,
            system: choice?.system,
          },
        });
      } else {
        this.response.answer[1] = {
          valueCoding: {
            code: value,
            display: choice?.display || value,
            system: choice?.system,
          },
        };
      }
    } else {
      // Regular number input
      if (this.response.answer.length === 0) {
        this.response.answer.push({
          valueCoding: {
            code: value,
            display: value,
            system: "http://example.com",
          },
        });

        // Add default unit if this is a number+unit input
        if (this.question.options?.reference && this.choices.length > 0) {
          this.response.answer.push({
            valueCoding: {
              code: this.choices[0].code,
              display: this.choices[0].display,
              system: this.choices[0].system,
            },
          });
        }
      } else {
        this.response.answer[0] = {
          valueCoding: {
            code: value,
            display: value,
            system: "http://example.com",
          },
        };
      }
    }

    this.answerChange.emit();
  }

  // Date input
  onDateChange(date: Date): void {
    this.response.answer = [];

    const dateString = format(date, "yyyy/MM/dd");
    const answer: IAnswer = {
      valueCoding: {
        code: dateString,
        display: dateString,
        system: "http://example.com",
      },
    };

    this.response.answer.push(answer);
    this.answerChange.emit();
  }

  getDateValue(): Date | null {
    const dateString = this.response.answer?.[0]?.valueCoding.code || this.question.answerDisplay?.default;

    if (dateString) {
      return parse(dateString, "yyyy/MM/dd", new Date());
    }
    return null;
  }

  // Rating input
  onRatingChange(rating: number): void {
    this.response.answer = [];

    const answer: IAnswer = {
      valueCoding: {
        code: rating.toString(),
        display: rating.toString(),
        system: "http://example.com",
      },
    };

    this.response.answer.push(answer);
    this.answerChange.emit();
  }

  private getRatingValue(): number {
    const value = this.response.answer?.[0]?.valueCoding.code;
    if (value) {
      return Number(value);
    }
    return Number(this.question.answerDisplay?.default || 0);
  }

  private getRatingStars(): number[] {
    const max = Number(this.question.answerDisplay?.max || 5);
    return Array.from({ length: max }, (_, i) => i + 1);
  }
}
